import type { LocaleType } from "@/constants/locales";
import { getLocaleCurrency } from "@/utils/location";
import type { EventProduct } from "@/types/discover";
import { formatCurrency } from "@/utils/currency";
import type { TranslationValues } from "use-intl/core";

export type EventPriceInfo = {
  hasMultiplePrices: boolean;
  hasTieredPrices: boolean;
  lowestPriceCents: number | null;
  basePriceCents: number | null;
  display: string;
};

function isFinitePrice(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value >= 0
  );
}

function isPriceLikeKey(key: string): boolean {
  return key.toLowerCase().includes("price");
}

function collectNestedPriceCandidates(
  value: unknown,
  parentKey?: string,
): number[] {
  if (isFinitePrice(value) && parentKey && isPriceLikeKey(parentKey)) {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      collectNestedPriceCandidates(entry, parentKey),
    );
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).flatMap(
      ([key, nested]) => collectNestedPriceCandidates(nested, key),
    );
  }

  return [];
}

function getProductPriceCandidates(product: EventProduct): number[] {
  const candidates = [
    ...collectNestedPriceCandidates(product.price, "price"),
    ...collectNestedPriceCandidates(product.priceModel, "priceModel"),
    ...collectNestedPriceCandidates(product),
  ];

  return [...new Set(candidates)];
}

function mergeProductLists(
  ...priceLists: Array<EventProduct[] | undefined>
): EventProduct[] {
  return priceLists.flatMap((list) => (Array.isArray(list) ? list : []));
}

function getLowestProductPriceCents(priceList: EventProduct[]): number | null {
  if (!Array.isArray(priceList) || priceList.length === 0) {
    return null;
  }
  let minCents = Number.POSITIVE_INFINITY;
  for (const product of priceList) {
    if (product.deleted) continue;
    for (const candidate of getProductPriceCandidates(product)) {
      if (candidate < minCents) {
        minCents = candidate;
      }
    }
  }
  if (minCents === Number.POSITIVE_INFINITY || !Number.isFinite(minCents)) {
    return null;
  }
  return minCents;
}

function getLowestPaidProductPriceCents(
  priceList: EventProduct[],
): number | null {
  if (!Array.isArray(priceList) || priceList.length === 0) {
    return null;
  }
  let minCents = Number.POSITIVE_INFINITY;
  for (const product of priceList) {
    if (product.deleted) continue;
    for (const candidate of getProductPriceCandidates(product)) {
      if (candidate > 0 && candidate < minCents) {
        minCents = candidate;
      }
    }
  }
  if (minCents === Number.POSITIVE_INFINITY || !Number.isFinite(minCents)) {
    return null;
  }
  return minCents;
}

export function getEventPriceInfo(
  {
    price_list,
    optional_price_list = [],
    price_cents,
    currency,
  }: {
    price_list: EventProduct[];
    optional_price_list?: EventProduct[];
    price_cents: number;
    /** Falls back to the currency of the page locale's market. */
    currency?: string;
  },
  t: (key: string, values?: TranslationValues) => string,
  locale: LocaleType,
): EventPriceInfo {
  const resolvedCurrency = currency ?? getLocaleCurrency(locale);
  const format = (amount: number) =>
    formatCurrency(resolvedCurrency, amount, locale);
  const allProducts = mergeProductLists(price_list, optional_price_list);
  const hasMultiplePrices = allProducts.length > 1;
  const hasTieredPrices = Array.isArray(allProducts)
    ? allProducts.some((price) => getProductPriceCandidates(price).length > 1)
    : false;
  const lowestFromProducts = getLowestProductPriceCents(allProducts);
  const lowestPaidProductPrice = getLowestPaidProductPriceCents(allProducts);
  const basePriceCents = typeof price_cents === "number" ? price_cents : null;
  const lowestDisplayableFromPrice =
    lowestFromProducts === 0 && lowestPaidProductPrice
      ? lowestPaidProductPrice
      : lowestFromProducts;

  let display = t("price.free");
  if (basePriceCents) {
    if (hasMultiplePrices || hasTieredPrices) {
      const resolved = lowestDisplayableFromPrice ?? basePriceCents;
      display = `${t("price.from")} ${format(resolved / 100)}`;
    } else {
      display = format(basePriceCents / 100);
    }
  } else if (lowestPaidProductPrice) {
    display = `${t("price.from")} ${format(lowestPaidProductPrice / 100)}`;
  }

  return {
    hasMultiplePrices,
    hasTieredPrices,
    lowestPriceCents: lowestFromProducts,
    basePriceCents,
    display,
  };
}
